/**
 * Backoff schedule for confirming a just-completed purchase.
 *
 * The failure this exists for is NOT an error response. `check-purchase-state` gates its
 * Stripe lookup on a `stripe_customer_id` that the checkout webhook writes, so before that
 * webhook lands the endpoint answers **200 OK with legitimately-free entitlements**. No
 * amount of error handling catches that — the only fix is to ask again.
 *
 * The previous single 600ms retry was far shorter than webhook propagation, so a paying
 * customer routinely landed on the success page still looking free.
 */
export const PURCHASE_CONFIRM_DELAYS_MS = [
  0, 1_000, 2_000, 4_000, 8_000, 15_000,
] as const;

/**
 * Delay before attempt `attempt` (0-based), or `null` when the ceiling is reached and the
 * caller should stop and say so plainly rather than spinning forever.
 */
export function getPurchaseConfirmDelay(attempt: number): number | null {
  if (!Number.isInteger(attempt) || attempt < 0) return null;
  return attempt < PURCHASE_CONFIRM_DELAYS_MS.length
    ? PURCHASE_CONFIRM_DELAYS_MS[attempt]
    : null;
}

/** Total time spent waiting if every attempt is exhausted. */
export function totalPurchaseConfirmWindowMs(): number {
  return PURCHASE_CONFIRM_DELAYS_MS.reduce((sum, ms) => sum + ms, 0);
}
